import { z } from 'zod';

import { isLiveSession } from '../../_shared/domain/enrichment-status.domain';
import { enrichmentSessionSchema } from '../../_shared/domain/session.domain';

/**
 * Domain model for the admin Session Detail page (GET /enrichment-sessions/:id).
 *
 * The response is a superset of the shared session contract: the same base fields
 * (`enrichmentSessionSchema`) plus per-target results, the admin-action trail, and
 * live-progress markers. Only this feature consumes the extra fields, so they are
 * declared here rather than in `_shared`. Pure (no React/Redux/fetch); all schemas
 * tolerate partial/absent data so a degraded row never throws at the boundary.
 */

/**
 * One (step, target) outcome, straight from the backend's `enrichment_step_results`
 * ledger. A target does NOT have a single outcome; it has one per pipeline step
 * (typically a profile step then a company step), and this is the real payload.
 *
 * `outcome` is null when the ledger holds no row for that pair: the target has not
 * reached that step yet, the run died before it did, or the pipeline predates the
 * ledger. Kept as a plain string, like `status` below, so an unforeseen backend
 * value renders instead of failing validation at the boundary.
 */
export const targetStepResultSchema = z.object({
  step_id: z.string(),
  position: z.number().default(0),
  connector: z.string().default(''),
  outcome: z.string().nullable().default(null),
  reason: z.string().nullable().default(null),
  created_at: z.string().nullable().default(null),
});

export type TargetStepResult = z.infer<typeof targetStepResultSchema>;

/**
 * One row of `profile_results`; a target and what happened to it at each step.
 *
 * `status` / `reason` are the aggregate the table chip and the header counters use.
 * The backend derives them from `steps` and sends them alongside, so the two are
 * never computed twice from the same data: any ERROR wins; a missing outcome on a
 * finished pipeline is a retryable `error`, unless the step's ledger is blind (it
 * ran and recorded nothing at all) in which case it is `unrecorded`, and no
 * per-target action may be built on it.
 */
export const targetResultSchema = z.object({
  target_id: z.string(),
  full_name: z.string().default(''),
  linkedin_url: z.string().nullable().default(null),
  linkedin_id: z.string().nullable().default(null),
  steps: z.array(targetStepResultSchema).default([]),
  // Kept as a plain string (not an enum) so an unforeseen backend status still
  // renders instead of failing validation; the UI colour-maps known values.
  status: z.string(),
  reason: z.string().optional(),
});

export type TargetResult = z.infer<typeof targetResultSchema>;

/**
 * One pipeline step, as the detail endpoint emits it; the whole row, not the
 * three fields the list carries.
 *
 * The page draws a card per step, so it needs each step's own status, counters and
 * time window. `config` is here for one reason: it is what tells two steps apart
 * when they run the same connector, which is the case that makes a session read as
 * "Profiles → Profiles" with nothing to distinguish the halves. Every field
 * defaults, so a step the backend sends half-populated renders
 * rather than throwing at the boundary.
 */
export const sessionStepDetailSchema = z.object({
  id: z.string(),
  connector: z.string().default(''),
  position: z.number().default(0),
  status: z.string().default(''),
  total_jobs: z.number().default(0),
  completed_jobs: z.number().default(0),
  failed_jobs: z.number().default(0),
  skipped_jobs: z.number().default(0),
  config: z.record(z.string(), z.unknown()).default({}),
  started_at: z.string().nullable().default(null),
  completed_at: z.string().nullable().default(null),
});

export type SessionStepDetail = z.infer<typeof sessionStepDetailSchema>;

/** One entry of the admin-action trail (create / pause / resume / …). */
export const adminActionSchema = z.object({
  id: z.string(),
  action: z.string(),
  admin_name: z.string().default(''),
  created_at: z.string(),
});

export type AdminAction = z.infer<typeof adminActionSchema>;

/** The full detail contract; the shared base extended with detail-only fields. */
export const sessionDetailSchema = enrichmentSessionSchema.extend({
  // `licence_id` / `list_id` now come from the shared base. The detail endpoint does not
  // resolve `licence_name` / `list_name`, which the base defaults to null.
  //
  // `steps` widens the base's three-field shape: the list draws pips, the detail
  // draws cards. A legacy session still carries none, hence the default.
  steps: z.array(sessionStepDetailSchema).default([]),
  current_step: z.string().nullable().default(null),
  current_profile: z.string().nullable().default(null),
  profile_results: z.array(targetResultSchema).default([]),
  admin_actions: z.array(adminActionSchema).default([]),
});

export type SessionDetail = z.infer<typeof sessionDetailSchema>;

// ── Status sets (single source of truth for the header-action visibility) ──

/**
 * Terminal statuses whose targets may be re-enqueued one by one.
 *
 * This is the server's list, not one of our own: `retryTargets`
 * (`backend/src/services/pipelineService.ts`) rejects everything else with
 * `Cannot retry targets on a pipeline in status X`. It used to omit `CANCELLED`,
 * so the front was narrower than the endpoint it calls for no stated reason;
 * and `LAUNCHABLE_STATUSES` below already offers a whole-run retry on a
 * cancelled collection, which made the omission incoherent on its own page.
 */
export const RETRYABLE_STATUSES = [
  'COMPLETED',
  'FAILED',
  'PARTIALLY_FAILED',
  'CANCELLED',
] as const;
/** Statuses from which the whole collection can be (re)launched from scratch. */
export const LAUNCHABLE_STATUSES = ['PENDING', 'FAILED', 'PARTIALLY_FAILED', 'CANCELLED'] as const;
/** Statuses that can be paused/resumed and therefore cancelled. */
export const CANCELLABLE_STATUSES = ['IN_PROGRESS', 'PAUSED'] as const;

// Permanent errors that should NOT be retried (bad URL, profile doesn't exist).
const PERMANENT_ERROR_PATTERNS = ['not found', 'inaccessible'];

const includesStatus = (set: readonly string[], status: string) => set.includes(status);

// `progressPercent` is gone with the "Progress" block it fed. It counted the
// session's own `*_count` columns, which count targets, while every bar on the
// page counts step results off the ledger, so the page carried two figures for
// "how far along", derived from two different sources, that could disagree.

/**
 * The `/in/…` segment of a LinkedIn URL; the only part of it that differs
 * between two rows.
 *
 * The column used to print the whole address, whose first 28 characters are
 * `https://www.linkedin.com/in/` on every single row. That is 40% of the table's
 * width spent restating a constant, taken from the columns that carry the answer.
 * A URL that does not match the expected shape is stripped of its scheme and
 * shown as-is rather than dropped: an odd address is worth seeing.
 */
export function linkedinHandle(url: string | null): string | null {
  if (!url) return null;
  const inSegment = /\/in\/([^/?#]+)/.exec(url);
  if (inSegment) return decodeURIComponent(inSegment[1]);
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '');
}

/**
 * Whether the page should keep live-polling; the rule the sessions list and the
 * KPI block already apply, now applied here too. A running status whose last sign
 * of life is recent, falling back to `created_at` for a run that never started,
 * which is exactly what a reset leaves behind.
 */
export function shouldAutoRefresh(session: SessionDetail, nowMs: number): boolean {
  return isLiveSession(session, nowMs);
}

/** The Cancel action is available while running or paused. */
export function canCancel(status: string): boolean {
  return includesStatus(CANCELLABLE_STATUSES, status);
}

/**
 * The Launch/Retry action is available from a launchable status; and from those
 * only.
 *
 * It used to also appear on any `IN_PROGRESS` session older than 30 minutes, on
 * the theory that such a run was stuck. A collection of a few thousand profiles
 * crosses that line while working perfectly, and the backend accepted the retry:
 * every target was re-enqueued, from zero, while the first wave was still
 * running; twice the API bill and counters incremented by both waves. The
 * backend now refuses `IN_PROGRESS` outright (#460), and a run that really is
 * wedged is moved to `FAILED` / `PARTIALLY_FAILED` by the watchdog after 10
 * minutes without activity, which is a launchable status.
 */
export function canLaunch(status: string): boolean {
  return includesStatus(LAUNCHABLE_STATUSES, status);
}

/** A reason is "permanent" (not worth retrying) when it matches a known pattern. */
export function isPermanentError(reason?: string): boolean {
  if (!reason) return false;
  const lower = reason.toLowerCase();
  return PERMANENT_ERROR_PATTERNS.some((p) => lower.includes(p));
}

/**
 * Failed targets that are worth retrying (excludes permanent errors).
 *
 * `error` and nothing else. A target the backend marks `unrecorded` is one whose
 * step ran before outcomes were recorded per target: the job counters know how
 * many failed, not which, and this list is a list of ids.
 */
export function retryableErrors(profileResults: TargetResult[]): TargetResult[] {
  return profileResults.filter((r) => r.status === 'error' && !isPermanentError(r.reason));
}

/**
 * Whether named targets can be re-enqueued on this run at all.
 *
 * The page has two ways in to `POST /enrichment-pipelines/:id/retry-targets`:
 * "Retry N errors" in the header, and "Retry selected" over a checkbox
 * selection; and until #465 only the first one asked this question. The second
 * offered itself on a run that had not started or was still going, and the
 * refusal arrived as a toast after the click, on a page where a click costs API
 * credits. One endpoint, one rule, read by both.
 */
export function canRetryTargets(status: string): boolean {
  return includesStatus(RETRYABLE_STATUSES, status);
}

/**
 * Why a targeted retry is unavailable, or `null` when it is available.
 *
 * A sentence per status rather than one for all three, because "not yet" and
 * "not until you stop it" are not corrected the same way, and the reader is
 * about to act on the answer. It replaces `retrySentence` in the selection bar:
 * announcing "Re-runs 2 selected targets from step 1, 2 recorded outcomes are
 * discarded" next to a button that cannot run is the defect, not the button.
 */
export function retryTargetsBlockedReason(status: string): string | null {
  if (canRetryTargets(status)) return null;
  switch (status) {
    case 'PENDING':
      return 'Retrying named targets needs a run that has started; launch this one first.';
    case 'PAUSED':
      return 'Retrying named targets needs a finished run; resume or cancel this one first.';
    default:
      return 'Retrying named targets needs a finished run; this one is still in progress.';
  }
}

/** Whether the "Retry N errors" action should show for this session. */
export function canRetryErrors(session: SessionDetail): boolean {
  return canRetryTargets(session.status) && retryableErrors(session.profile_results).length > 0;
}

/** Human label for an admin-action trail entry. */
export function actionLabel(action: string): string {
  switch (action) {
    case 'create':
      return 'Session created';
    case 'pause':
      return 'Session paused';
    case 'resume':
      return 'Session resumed';
    case 'retry':
      return 'Session retried';
    case 'cancel':
      return 'Session cancelled';
    case 'reset':
      return 'Session reset';
    default:
      return action;
  }
}
